"use client";

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock3 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { footballDataCodeForLeagueSlug } from "@/lib/football-competitions";
import type { FootballLeagueStandings, FootballScoreboardFreshness, FootballScoreboardMatch } from "@/lib/football-scoreboard-data";
import { getResponsiveTeamLabel } from "@/lib/responsive-team-label";
import { getHungarianWorldCupTeamName } from "@/lib/world-cup-team-names";

const weekOffsets = [-1, 0, 1] as const;
const hungarianTimeZone = "Europe/Budapest";
type WeekOffset = (typeof weekOffsets)[number];
type ScoreboardLeagueOption = {
  slug: string;
  name: string;
};

const supportedLeagueSlugs = [
  "world-cup",
  "premier-league",
  "la-liga",
  "serie-a",
  "bundesliga",
  "bajnokok-ligaja",
  "europa-liga",
] as const;

function teamLabel(team: FootballScoreboardMatch["homeTeam"]) {
  const name = getHungarianWorldCupTeamName({
    name: team?.name,
    shortName: team?.shortName,
    code: team?.tla,
    tla: team?.tla,
  }).trim();
  if (!name) return "TBA";

  return name
    .replace(/^1\.\s*FSV\s+Mainz\s+05$/i, "Mainz 05")
    .replace(/^1\.\s*FC\s+Heidenheim(?:\s+1846)?$/i, "Heidenheim")
    .replace(/^SV\s+Werder\s+Bremen$/i, "Werder Bremen")
    .replace(/^BV\s+Borussia\s+09\s+Dortmund$/i, "Borussia Dortmund")
    .replace(/^FC\s+Bayern\s+München$/i, "Bayern München")
    .replace(/^Bayer\s+04\s+Leverkusen$/i, "Bayer Leverkusen");
}

function teamLabels(team: FootballScoreboardMatch["homeTeam"]) {
  const displayTeam = {
    name: teamLabel(team),
    shortName: team?.shortName,
    tla: team?.tla,
  };

  return {
    desktop: getResponsiveTeamLabel(displayTeam, "desktop"),
    tablet: getResponsiveTeamLabel(displayTeam, "tablet"),
    mobile: getResponsiveTeamLabel(displayTeam, "mobile"),
  };
}

function startOfWeek(date: Date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const day = start.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + mondayOffset);

  return start;
}

function weekRange(offset: WeekOffset) {
  const start = startOfWeek(new Date());
  start.setDate(start.getDate() + offset * 7);

  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  return { start, end };
}

function monthLabel(date: Date) {
  return new Intl.DateTimeFormat("hu-HU", { month: "short" }).format(date);
}

function weekLabel(start: Date, endExclusive: Date) {
  const end = new Date(endExclusive);
  end.setDate(end.getDate() - 1);

  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return `${start.getFullYear()}. ${monthLabel(start)} ${start.getDate()}–${end.getDate()}.`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${start.getFullYear()}. ${monthLabel(start)} ${start.getDate()}.–${monthLabel(end)} ${end.getDate()}.`;
  }

  return `${start.getFullYear()}. ${monthLabel(start)} ${start.getDate()}.–${end.getFullYear()}. ${monthLabel(end)} ${end.getDate()}.`;
}

function formattedKickoff(match: FootballScoreboardMatch) {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(match.utcDate));
}

function compactKickoff(match: FootballScoreboardMatch) {
  const kickoff = new Date(match.utcDate);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const numericDateParts = new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(kickoff);
  const numericMonth = numericDateParts.find((part) => part.type === "month")?.value ?? "";
  const numericDay = numericDateParts.find((part) => part.type === "day")?.value ?? "";
  const numericDateLabel = `${numericMonth}.${numericDay}`;

  const dateLabel = isSameLocalDay(kickoff, today)
    ? "MA"
    : isSameLocalDay(kickoff, tomorrow)
      ? "HOLNAP"
      : numericDateLabel;

  const timeLabel = new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(kickoff);

  return `${dateLabel} • ${timeLabel}`;
}

function competitionShortLabel(match: FootballScoreboardMatch) {
  const code = match.competition?.code;
  if (code) return code;

  const name = match.competition?.name ?? "";
  if (name.toLowerCase().includes("premier league")) return "PL";
  if (name.toLowerCase().includes("bundesliga")) return "BL1";
  if (name.toLowerCase().includes("serie a")) return "SA";
  if (name.toLowerCase().includes("laliga") || name.toLowerCase().includes("primera")) return "LL";

  return "Liga";
}

function competitionMatchesLeague(match: FootballScoreboardMatch, activeLeagueSlug?: string): boolean {
  const code = match.competition?.code?.toUpperCase() ?? "";
  const name = match.competition?.name.toLowerCase() ?? "";

  if (!activeLeagueSlug) {
    return supportedLeagueSlugs.some((leagueSlug) => competitionMatchesLeague(match, leagueSlug));
  }

  const expectedCode = footballDataCodeForLeagueSlug(activeLeagueSlug);
  if (expectedCode && code === expectedCode) return true;

  switch (activeLeagueSlug) {
    case "premier-league":
      return name.includes("premier league");
    case "la-liga":
      return code === "LALIGA" || name.includes("laliga") || name.includes("primera division");
    case "serie-a":
      return name.includes("serie a");
    case "bundesliga":
      return name.includes("bundesliga");
    case "bajnokok-ligaja":
      return name.includes("champions league");
    case "europa-liga":
      return name.includes("europa league");
    case "world-cup":
      return code === "WC" || name.includes("world cup") || name.includes("világbajnokság");
    default:
      return false;
  }
}

function matchInvolvesTeam(match: FootballScoreboardMatch, activeTeamSlug?: string): boolean {
  if (!activeTeamSlug) return true;

  return match.homeTeam?.mappedTeamSlug === activeTeamSlug || match.awayTeam?.mappedTeamSlug === activeTeamSlug;
}

function isLive(status: string) {
  return status === "LIVE" || status === "IN_PLAY" || status === "PAUSED";
}

function isFinished(status: string) {
  return status === "FINISHED" || status === "FT";
}

function freshnessLabel(iso: string | null | undefined) {
  if (!iso) return null;

  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function scoreLabel(match: FootballScoreboardMatch) {
  if (match.homeScore === null || match.awayScore === null) return "–";
  return `${match.homeScore}–${match.awayScore}`;
}

function statusLabel(match: FootballScoreboardMatch) {
  if (isLive(match.status)) return "LIVE";
  if (isFinished(match.status)) return "FT";
  return compactKickoff(match);
}

function hungarianDateKey(date: Date) {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function isSameLocalDay(left: Date, right: Date) {
  return hungarianDateKey(left) === hungarianDateKey(right);
}

function MatchState({ match }: { match: FootballScoreboardMatch }) {
  if (isLive(match.status)) {
    return (
      <span className="flex flex-col items-center gap-0.5">
        <span className="rounded-sm bg-[#c92424] px-1 py-0.5 text-[8px] font-black leading-none text-white">
          LIVE
        </span>
        <span className="font-black tabular-nums text-[#111111]">{scoreLabel(match)}</span>
      </span>
    );
  }

  if (isFinished(match.status)) {
    return (
      <span className="flex flex-col items-center gap-0.5">
        <span className="text-[9px] font-black uppercase text-[#6B6B6B]">FT</span>
        <span className="font-black tabular-nums text-[#111111]">{scoreLabel(match)}</span>
      </span>
    );
  }

  return <span className="text-center text-[10px] font-bold text-[#6B6B6B]">{formattedKickoff(match)}</span>;
}

function Crest({ src, alt }: { src: string | null | undefined; alt: string }) {
  if (!src) return null;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className="size-3.5 shrink-0 object-contain"
      loading="lazy"
      decoding="async"
    />
  );
}

function TeamCell({
  team,
  side = "home",
}: {
  team: FootballScoreboardMatch["homeTeam"];
  side?: "home" | "away";
}) {
  const label = teamLabel(team);
  const labels = teamLabels(team);
  const isAway = side === "away";

  return (
    <span
      className={`flex w-[5.5rem] min-w-0 items-center gap-1.5 sm:w-[13rem] ${
        isAway ? "justify-end text-right" : "justify-start text-left"
      }`}
    >
      {!isAway ? <Crest src={team?.crest} alt={`${label} logo`} /> : null}
      <span className="block shrink-0 tabular-nums sm:hidden">{labels.mobile}</span>
      <span className="hidden min-w-0 truncate sm:block lg:hidden">{labels.tablet}</span>
      <span className="hidden min-w-0 truncate lg:block">{labels.desktop}</span>
      {isAway ? <Crest src={team?.crest} alt={`${label} logo`} /> : null}
    </span>
  );
}

function CompactMatchItem({ match }: { match: FootballScoreboardMatch }) {
  const home = teamLabel(match.homeTeam);
  const away = teamLabel(match.awayTeam);
  const homeLabels = teamLabels(match.homeTeam);
  const awayLabels = teamLabels(match.awayTeam);
  const isScored = isLive(match.status) || isFinished(match.status);
  const stateLabel = statusLabel(match);
  const mainState = isScored ? scoreLabel(match) : stateLabel;
  const [kickoffDateLabel, kickoffTimeLabel] = isScored ? [] : stateLabel.split(" • ");
  const mobileKickoffDateLabel = kickoffDateLabel === "HOLNAP" ? "HOL." : kickoffDateLabel;

  return (
    <article className="inline-grid h-10 w-[calc(100vw-32px)] min-w-[calc(100vw-32px)] shrink-0 grid-cols-[minmax(0,1fr)_4rem_minmax(0,1fr)] items-center gap-1 rounded-sm border border-[#D8C99B] bg-white px-2 text-xs leading-none text-[#111111] sm:w-[220px] sm:min-w-0 sm:grid-cols-[auto_minmax(0,1fr)_5.5rem_minmax(0,1fr)] sm:gap-1.5 sm:px-2 sm:text-sm lg:w-[258px] xl:w-[320px]">
      <span className="hidden shrink-0 rounded-sm bg-[#F6F1E3] px-1 py-1 text-[10px] font-black uppercase leading-none text-[#A4243B] sm:inline">
        {competitionShortLabel(match)}
      </span>
      <span className="flex min-w-0 items-center gap-1 overflow-hidden font-semibold" title={match.homeTeam?.name ?? home}>
        <Crest src={match.homeTeam?.crest} alt={`${home} logo`} />
        <span className="block shrink-0 tabular-nums sm:hidden">{homeLabels.mobile}</span>
        <span className="hidden min-w-0 truncate sm:block lg:hidden">{homeLabels.tablet}</span>
        <span className="hidden min-w-0 truncate lg:block">{homeLabels.desktop}</span>
      </span>
      {isScored ? (
        <span className="shrink-0 whitespace-nowrap text-center text-sm font-black tabular-nums text-[#111111]">
          {mainState}
        </span>
      ) : (
        <span className="max-w-16 shrink-0 overflow-hidden whitespace-nowrap text-center text-[9px] font-black tabular-nums sm:max-w-none sm:text-[11px]" title={stateLabel}>
          <span className="text-[#6B6B6B] sm:hidden">{mobileKickoffDateLabel}</span>
          <span className="hidden text-[#6B6B6B] sm:inline">{kickoffDateLabel}</span>
          <span className="px-0.5 text-[#9A958A]">•</span>
          <span className="text-[#111111]">{kickoffTimeLabel}</span>
        </span>
      )}
      <span className="flex min-w-0 items-center justify-end gap-1 overflow-hidden text-right font-semibold" title={match.awayTeam?.name ?? away}>
        <span className="block shrink-0 tabular-nums sm:hidden">{awayLabels.mobile}</span>
        <span className="hidden min-w-0 truncate sm:block lg:hidden">{awayLabels.tablet}</span>
        <span className="hidden min-w-0 truncate lg:block">{awayLabels.desktop}</span>
        <Crest src={match.awayTeam?.crest} alt={`${away} logo`} />
      </span>
    </article>
  );
}

export function FootballScoreboard({
  matches,
  activeLeagueSlug,
  activeTeamSlug,
  standings,
  standingsLeagueOptions,
  standingsByLeague,
  freshness: scoreboardFreshness,
  canRefresh = false,
}: {
  matches: FootballScoreboardMatch[];
  activeLeagueSlug?: string;
  activeTeamSlug?: string;
  standings?: FootballLeagueStandings | null;
  standingsLeagueOptions?: ScoreboardLeagueOption[];
  standingsByLeague?: Record<string, FootballLeagueStandings | null>;
  freshness?: FootballScoreboardFreshness | null;
  canRefresh?: boolean;
}) {
  const router = useRouter();
  const [activeWeekOffset, setActiveWeekOffset] = useState<WeekOffset>(0);
  const [expandedPanel, setExpandedPanel] = useState<"matches" | "standings" | null>(null);
  const [selectedStandingsLeagueSlug, setSelectedStandingsLeagueSlug] = useState<string | null>(null);
  const [renderedAt] = useState(() => Date.now());
  const [manualRefreshState, setManualRefreshState] = useState<{
    loading: boolean;
    message: string | null;
    error: boolean;
    refreshedAt: string | null;
  }>({
    loading: false,
    message: null,
    error: false,
    refreshedAt: null,
  });
  const [viewerCanRefresh, setViewerCanRefresh] = useState(canRefresh);

  const selectedStandings = useMemo(() => {
    if (activeLeagueSlug) return standings ?? null;
    if (!selectedStandingsLeagueSlug) return null;
    return standingsByLeague?.[selectedStandingsLeagueSlug] ?? null;
  }, [activeLeagueSlug, selectedStandingsLeagueSlug, standings, standingsByLeague]);
  const activeWeek = useMemo(() => weekRange(activeWeekOffset), [activeWeekOffset]);
  const canGoPrevious = activeWeekOffset > weekOffsets[0];
  const canGoNext = activeWeekOffset < weekOffsets[weekOffsets.length - 1];
  const activeWeekMatches = useMemo(
    () =>
      matches.filter((match) => {
        const matchDate = new Date(match.utcDate);

        return (
          matchDate >= activeWeek.start &&
          matchDate < activeWeek.end &&
          competitionMatchesLeague(match, activeLeagueSlug) &&
          matchInvolvesTeam(match, activeTeamSlug)
        );
      }),
    [activeLeagueSlug, activeTeamSlug, activeWeek, matches],
  );
  const relevantMatches = useMemo(
    () =>
      matches.filter(
        (match) => competitionMatchesLeague(match, activeLeagueSlug) && matchInvolvesTeam(match, activeTeamSlug),
      ),
    [activeLeagueSlug, activeTeamSlug, matches],
  );
  const latestFinishedMatches = useMemo(
    () =>
      [...relevantMatches]
        .filter((match) => isFinished(match.status))
        .sort((first, second) => Date.parse(second.utcDate) - Date.parse(first.utcDate)),
    [relevantMatches],
  );
  const activeMatches = useMemo(
    () => (activeWeekMatches.length ? activeWeekMatches : latestFinishedMatches).slice(0, 8),
    [activeWeekMatches, latestFinishedMatches],
  );
  const isRenderingFinishedFallback = activeWeekMatches.length === 0 && latestFinishedMatches.length > 0;
  const compactMatches = useMemo(() => {
    const now = new Date();
    const todayMatches = relevantMatches.filter((match) =>
      isSameLocalDay(new Date(match.utcDate), now),
    );

    if (todayMatches.length) {
      return { label: "Mai meccsek", matches: todayMatches };
    }

    const upcomingMatches = relevantMatches.filter((match) => new Date(match.utcDate) >= now);
    if (upcomingMatches.length) {
      return { label: "Következő meccsek", matches: upcomingMatches.slice(0, 3) };
    }

    return {
      label: "Eredmények",
      matches: latestFinishedMatches.slice(0, 3),
    };
  }, [latestFinishedMatches, relevantMatches]);
  const leagueMatches = useMemo(
    () => matches.filter((match) => competitionMatchesLeague(match, activeLeagueSlug)),
    [activeLeagueSlug, matches],
  );
  const availableCompetitionCounts = useMemo(
    () =>
      matches.reduce<Record<string, number>>((counts, match) => {
        const key = match.competition?.code ?? match.competition?.name ?? "unknown";
        counts[key] = (counts[key] ?? 0) + 1;
        return counts;
      }, {}),
    [matches],
  );
  const fixtureEmptyState = useMemo(() => {
    if (!matches.length) {
      return "Nem érkezett meccsadat a szervertől.";
    }

    if (!leagueMatches.length) {
      return "Érkezett meccsadat, de ehhez a ligához nincs találat.";
    }

    if (activeTeamSlug && !relevantMatches.length) {
      return "A kiválasztott csapathoz nincs illesztett meccsadat.";
    }

    if (!activeWeekMatches.length && latestFinishedMatches.length) {
      return "Legutóbbi befejezett meccsek.";
    }

    if (!activeWeekMatches.length) {
      return "Ehhez a héthez nincs megjeleníthető meccs.";
    }

    return "Nincs megjeleníthető meccs.";
  }, [
    activeTeamSlug,
    activeWeekMatches.length,
    latestFinishedMatches.length,
    leagueMatches.length,
    matches.length,
    relevantMatches.length,
  ]);
  const compactEmptyState = useMemo(() => {
    if (!matches.length) {
      return "Nem érkezett meccsadat a szervertől.";
    }

    if (!leagueMatches.length) {
      return "Érkezett meccsadat, de ehhez a ligához nincs találat.";
    }

    if (activeTeamSlug && !relevantMatches.length) {
      return "A kiválasztott csapathoz nincs illesztett meccsadat.";
    }

    return "Nincs következő megjeleníthető meccs.";
  }, [activeTeamSlug, leagueMatches.length, matches.length, relevantMatches.length]);
  const latestMatchesFetch = useMemo(() => {
    const timestamps = relevantMatches
      .map((match) => Date.parse(match.fetchedAt))
      .filter((value) => Number.isFinite(value));
    if (!timestamps.length) return null;
    return new Date(Math.max(...timestamps)).toISOString();
  }, [relevantMatches]);
  const matchesThresholdMinutes = useMemo(() => {
    if (relevantMatches.some((match) => isLive(match.status))) return 5;
    if (relevantMatches.some((match) => isSameLocalDay(new Date(match.utcDate), new Date()))) return 15;
    return 90;
  }, [relevantMatches]);
  const standingsThresholdMinutes = useMemo(() => {
    if (relevantMatches.some((match) => isLive(match.status))) return 30;
    if (relevantMatches.some((match) => isSameLocalDay(new Date(match.utcDate), new Date()))) return 90;
    return 480;
  }, [relevantMatches]);
  const matchesStale = useMemo(() => {
    if (!latestMatchesFetch) return false;
    return renderedAt - Date.parse(latestMatchesFetch) > matchesThresholdMinutes * 60_000;
  }, [latestMatchesFetch, matchesThresholdMinutes, renderedAt]);
  const standingsStale = useMemo(() => {
    if (!selectedStandings?.fetchedAt) return false;
    return renderedAt - Date.parse(selectedStandings.fetchedAt) > standingsThresholdMinutes * 60_000;
  }, [renderedAt, selectedStandings, standingsThresholdMinutes]);
  const freshness = expandedPanel === "standings" && selectedStandings?.fetchedAt
    ? {
        iso: manualRefreshState.refreshedAt ?? scoreboardFreshness?.refreshedAt ?? selectedStandings.fetchedAt,
        stale: manualRefreshState.refreshedAt ? false : standingsStale,
      }
    : manualRefreshState.refreshedAt || scoreboardFreshness?.refreshedAt || latestMatchesFetch
      ? {
          iso: manualRefreshState.refreshedAt ?? scoreboardFreshness?.refreshedAt ?? latestMatchesFetch,
          stale: manualRefreshState.refreshedAt ? false : matchesStale,
        }
      : null;
  const canShowRefresh = canRefresh || viewerCanRefresh;

  useEffect(() => {
    if (canRefresh) {
      return;
    }

    let cancelled = false;
    fetch("/api/header-viewer", {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled) return;
        const role = typeof payload?.viewerRole === "string" ? payload.viewerRole : null;
        setViewerCanRefresh(role === "ADMIN" || role === "EDITOR");
      })
      .catch(() => {
        if (!cancelled) setViewerCanRefresh(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canRefresh]);

  async function refreshScoreboard() {
    if (manualRefreshState.loading) return;

    setManualRefreshState((current) => ({
      ...current,
      loading: true,
      message: null,
      error: false,
    }));

    try {
      const response = await fetch("/admin/api/football-data/sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ force: true, competition: activeLeagueSlug ?? undefined }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(typeof payload.error === "string" ? payload.error : "A frissítés nem sikerült.");
      }

      const standingsErrors = Array.isArray(payload.standingsErrors) ? payload.standingsErrors.length : 0;
      setManualRefreshState({
        loading: false,
        message: standingsErrors ? "Hiba történt" : activeLeagueSlug === "world-cup" ? "Sikeres frissítés" : "Frissítve.",
        error: Boolean(standingsErrors),
        refreshedAt: new Date().toISOString(),
      });
      router.refresh();
    } catch (error) {
      setManualRefreshState({
        loading: false,
        message: activeLeagueSlug === "world-cup" ? "Hiba történt" : error instanceof Error ? error.message : "A frissítés nem sikerült.",
        error: true,
        refreshedAt: manualRefreshState.refreshedAt,
      });
    }
  }

  useEffect(() => {
    console.info("[match-hub:fixtures:client]", {
      selectedCompetitionId: activeLeagueSlug ?? null,
      selectedTeamId: activeTeamSlug ?? null,
      dateRange: {
        start: activeWeek.start.toISOString(),
        end: activeWeek.end.toISOString(),
      },
      rawApiResponseCount: matches.length,
      filteredFixtureCount: activeWeekMatches.length,
      finalRenderedFixtureCount: activeMatches.length,
      leagueFixtureCount: leagueMatches.length,
      selectedTeamFixtureCount: relevantMatches.length,
      compactRenderedFixtureCount: compactMatches.matches.length,
      availableCompetitionCounts: !leagueMatches.length ? availableCompetitionCounts : undefined,
    });
  }, [
    activeLeagueSlug,
    activeTeamSlug,
    activeMatches.length,
    activeWeekMatches.length,
    activeWeek.end,
    activeWeek.start,
    compactMatches.matches.length,
    availableCompetitionCounts,
    leagueMatches.length,
    matches.length,
    relevantMatches.length,
  ]);

  return (
    <section aria-label="Football meccsek" className="w-full max-w-full overflow-hidden border-y border-[#D8C99B] bg-white text-[#111111]">
      <div className="grid min-h-14 grid-cols-1 items-center overflow-hidden sm:max-h-[72px] sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <div className="hidden h-full w-[68px] items-center justify-center border-r border-[#e5e5e5] px-2 py-2 sm:flex sm:w-[92px] sm:px-3">
          <div className="flex min-h-10 min-w-0 items-center gap-1 sm:min-h-12 sm:gap-2">
            <h2 className="flex min-w-0 flex-col text-[8px] font-semibold uppercase leading-none tracking-[0.08em] text-zinc-400 sm:text-[9px] sm:tracking-[0.12em]">
              <span>MÉRKŐZÉSEK</span>
            </h2>
            <div className="h-px w-2 bg-zinc-200 sm:w-6" aria-hidden="true" />
          </div>
        </div>

        <div className="relative min-w-0 overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-4 bg-gradient-to-r from-white to-transparent" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-4 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />
          <div className="min-w-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div
              className={`flex min-h-14 max-w-none items-center gap-2 px-4 py-2 sm:min-h-16 sm:w-max sm:px-3 ${
                compactMatches.matches.length <= 1 ? "w-full justify-center" : "w-max"
              }`}
            >
              {compactMatches.matches.length ? (
                compactMatches.matches.map((match) => (
                  <CompactMatchItem key={match.id} match={match} />
                ))
              ) : (
                <p className="flex min-h-10 items-center text-sm font-semibold text-[#6B6B6B]">
                  {compactEmptyState}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex min-h-10 items-center border-t border-[#D8C99B] px-2 py-1.5 sm:h-full sm:min-h-16 sm:border-l sm:border-t-0 sm:px-2 sm:py-2">
          <div className="flex w-full items-center justify-end gap-1.5 sm:w-auto sm:gap-2">
            {freshness?.iso ? (
              <span
                className={`hidden items-center gap-1 text-[10px] font-semibold tabular-nums sm:inline-flex ${
                  freshness.stale ? "text-[#A66A00]" : "text-[#6B6B6B]"
                }`}
                title={freshness.stale ? "Az adatfrissítés késik." : "Legutóbbi adatfrissítés."}
              >
                <Clock3 size={11} aria-hidden="true" />
                {freshnessLabel(freshness.iso)}
              </span>
            ) : null}
            {canShowRefresh ? (
              <div className="flex min-w-0 items-center gap-1.5">
                {freshness?.iso ? (
                  <span className="text-[10px] font-semibold tabular-nums text-[#6B6B6B] sm:hidden">
                    {freshnessLabel(freshness.iso)}
                  </span>
                ) : null}
                {freshness?.iso ? <span className="text-[10px] font-semibold text-[#9A958A] sm:hidden">·</span> : null}
                <button
                  type="button"
                  className="h-7 shrink-0 rounded-sm border border-[#D8C99B] bg-white px-2 text-[10px] font-black uppercase tracking-wide text-[#273E47] outline-none transition-colors hover:bg-[#F6F1E3] disabled:cursor-wait disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-[#D8973C] focus-visible:ring-offset-1"
                  disabled={manualRefreshState.loading}
                  onClick={refreshScoreboard}
                >
                  {manualRefreshState.loading ? "Frissítés…" : activeLeagueSlug === "world-cup" ? "VB frissítése" : "Frissítés"}
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-sm border border-[#D8C99B] bg-white px-2 text-[10px] font-black uppercase tracking-wide text-[#273E47] outline-none transition-colors hover:bg-[#F6F1E3] focus-visible:ring-2 focus-visible:ring-[#D8973C] focus-visible:ring-offset-1 sm:h-9 sm:flex-none sm:px-3"
              aria-expanded={expandedPanel === "matches"}
              onClick={() => setExpandedPanel((current) => (current === "matches" ? null : "matches"))}
            >
              <span>Meccsek</span>
              {expandedPanel === "matches" ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            </button>
            <button
              type="button"
              className={`flex h-8 flex-1 items-center justify-center gap-1 rounded-sm border border-[#D8C99B] px-2 text-[10px] font-black uppercase tracking-wide outline-none transition-colors hover:bg-[#F6F1E3] focus-visible:ring-2 focus-visible:ring-[#D8973C] focus-visible:ring-offset-1 sm:h-9 sm:flex-none sm:px-3 ${
                expandedPanel === "standings" ? "bg-[#F6F1E3] text-[#A4243B]" : "bg-white text-[#273E47]"
              }`}
              aria-expanded={expandedPanel === "standings"}
              onClick={() => setExpandedPanel((current) => (current === "standings" ? null : "standings"))}
            >
              <span>Tabella</span>
              {expandedPanel === "standings" ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>
      {canShowRefresh && manualRefreshState.message ? (
        <p
          className={`border-t border-[#F0E5C4] px-3 py-1 text-right text-[11px] font-semibold ${
            manualRefreshState.error ? "text-[#A4243B]" : "text-[#28724F]"
          }`}
          role="status"
        >
          {manualRefreshState.message}
        </p>
      ) : null}

      {expandedPanel === "matches" ? (
        <div className="max-w-full overflow-hidden border-t border-[#d9d7cf]">
          <div className="grid grid-cols-[2rem_minmax(0,1fr)_2rem] items-center border-b border-[#d9d7cf]">
            <button
              type="button"
              aria-label="Előző hét"
              disabled={!canGoPrevious}
              className="flex min-h-8 items-center justify-center border-r border-[#e5e5e5] text-[#111111] disabled:cursor-not-allowed disabled:text-[#c9c5ba]"
              onClick={() => setActiveWeekOffset((offset) => Math.max(offset - 1, -1) as WeekOffset)}
            >
              <ChevronLeft size={15} aria-hidden="true" />
            </button>
            <div className="min-w-0 px-2 py-1.5 text-center">
              <p className="truncate text-[11px] font-black text-[#111111]">
                {isRenderingFinishedFallback
                  ? "Legutóbbi lejátszott mérkőzések"
                  : weekLabel(activeWeek.start, activeWeek.end)}
              </p>
              {isRenderingFinishedFallback ? (
                <p className="mx-auto mt-1 max-w-2xl text-[11px] font-semibold leading-snug text-[#6B6B6B]">
                  A szezon aktuális időablakában nincs közelgő mérkőzés, ezért a legutóbbi eredményeket mutatjuk.
                </p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Következő hét"
              disabled={!canGoNext}
              className="flex min-h-8 items-center justify-center border-l border-[#e5e5e5] text-[#111111] disabled:cursor-not-allowed disabled:text-[#c9c5ba]"
              onClick={() => setActiveWeekOffset((offset) => Math.min(offset + 1, 1) as WeekOffset)}
            >
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto overflow-x-hidden">
            {activeMatches.length ? (
              activeMatches.map((match) => (
                <article
                  key={match.id}
                  className="border-b border-[#e5e5e5] py-2 text-xs leading-tight last:border-b-0 sm:py-1.5"
                >
                  <div className="mx-auto flex w-full max-w-md items-center justify-center gap-3 px-3 sm:max-w-xl sm:gap-4">
                    <TeamCell team={match.homeTeam} />
                    <span className="shrink-0 text-center">
                      <MatchState match={match} />
                    </span>
                    <TeamCell team={match.awayTeam} side="away" />
                  </div>
                </article>
              ))
            ) : (
              <p className="px-3 py-5 text-sm font-semibold text-[#6B6B6B]">{fixtureEmptyState}</p>
            )}
          </div>
        </div>
      ) : null}

      {expandedPanel === "standings" ? (
        <div className="max-w-full overflow-hidden border-t border-[#d9d7cf]">
          {standingsLeagueOptions?.length ? (
            <div className="flex flex-wrap gap-2 border-b border-[#e5e5e5] px-3 py-2">
              {standingsLeagueOptions.map((league) => {
                const selected = selectedStandingsLeagueSlug === league.slug;

                return (
                  <button
                    key={league.slug}
                    type="button"
                    className={`rounded-sm border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                      selected
                        ? "border-[#D8973C] bg-[#F6F1E3] text-[#A4243B]"
                        : "border-[#D8C99B] bg-white text-[#273E47] hover:bg-[#F6F1E3]"
                    }`}
                    onClick={() => setSelectedStandingsLeagueSlug(league.slug)}
                  >
                    {league.name}
                  </button>
                );
              })}
            </div>
          ) : null}
          {selectedStandings?.rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-sm">
                <thead className="bg-[#F6F1E3] text-[#6B6B6B]">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-black uppercase">#</th>
                    <th className="px-3 py-2 text-left text-[11px] font-black uppercase">Csapat</th>
                    <th className="px-3 py-2 text-center text-[11px] font-black uppercase">M</th>
                    <th className="px-3 py-2 text-center text-[11px] font-black uppercase">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStandings.rows.map((row) => (
                    <tr key={`${selectedStandings.leagueSlug}-${row.rank}-${row.name}`} className="border-t border-[#e5e5e5]">
                      <td className="px-3 py-2 font-black text-[#6B6B6B]">{row.rank}</td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2 font-semibold text-[#111111]">
                          <Crest src={row.logoUrl} alt={`${row.name} logo`} />
                          <span>{row.name}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-[#6B6B6B]">{row.played ?? "–"}</td>
                      <td className="px-3 py-2 text-center font-black text-[#111111]">{row.points ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : standingsLeagueOptions?.length && !selectedStandingsLeagueSlug ? (
            <p className="px-3 py-5 text-sm font-semibold text-[#6B6B6B]">Válassz ligát a tabellához.</p>
          ) : (
            <p className="px-3 py-5 text-sm font-semibold text-[#6B6B6B]">Nincs elérhető tabellaadat.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
